import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Icon
} from '@mui/material';
import { 
  Person as PersonIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { PRIMARIO } from "src/styles/styles";

interface RecomendacionesProps {
  handleNext: () => void;
}

const Recomendaciones: React.FC<RecomendacionesProps> = ({ handleNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estilos condicionales basados en dispositivo móvil o desktop
  const titleStyle = {
    fontWeight: 'bold',
    color: PRIMARIO,
    textAlign: 'center',
    mb: isMobile ? 2 : 3,
    mt: isMobile ? 1 : 2,
    fontSize: isMobile ? '1.5rem' : '2rem'
  };

  const sectionTitleStyle = {
    fontWeight: 'bold',
    color: PRIMARIO,
    mb: isMobile ? 1 : 2,
    mt: isMobile ? 2 : 3,
    fontSize: isMobile ? '1.2rem' : '1.5rem'
  };

  const perfilCardStyle = {
    mb: 2,
    backgroundColor: '#f5f8ff',
    boxShadow: 1,
    borderLeft: `4px solid ${PRIMARIO}`
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, maxWidth: '900px', mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 4, borderRadius: 2 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={titleStyle}>
          Bienvenido al Proceso de Inscripción para Aspirantes
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, textAlign: 'justify' }}>
          Gracias por tu interés en formar parte de nuestro equipo de formadores. Este formulario te guiará a través del proceso de inscripción, que consta de 4 secciones principales:
        </Typography>

        <Box sx={{ my: 3 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={sectionTitleStyle}>
            Proceso de Inscripción
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Recomendaciones" 
                secondary="Información importante para completar tu inscripción correctamente" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PersonIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Información Básica" 
                secondary="Datos personales, contacto y perfil al que aspiras" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WorkIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Experiencia Profesional" 
                secondary="Detalle de tu trayectoria laboral" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DescriptionIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Documentos Requeridos" 
                secondary="Carga de documentos que respaldan tu formación y experiencia" 
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: isMobile ? 2 : 3 }} />

        <Typography variant={isMobile ? 'h6' : 'h5'} sx={sectionTitleStyle}>
          Requisitos para los Perfiles
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Te informamos que cada perfil tiene requisitos específicos de formación académica y experiencia:
        </Typography>

        <Card sx={perfilCardStyle}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              EJECUTOR TÉCNICO
            </Typography>
            <Typography variant="body2">
              Profesional en NBC (Matemáticas, estadística, ingeniería de sistemas, telemática o electrónica). 
              Requiere 3 años de experiencia: 2 años como docente/instructor y 1 año específico en el área de formación.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={perfilCardStyle}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              MENTOR
            </Typography>
            <Typography variant="body2">
              Profesional en NBC (Matemáticas, estadística, ingeniería de sistemas, telemática o electrónica). 
              Requiere 3 años de experiencia: 2 años como docente/instructor y 1 año específico en el área.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={perfilCardStyle}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              MONITOR
            </Typography>
            <Typography variant="body2">
              Tecnólogo, técnico o estudiante de últimos semestres en NBC (Matemáticas, estadística, ingeniería). 
              Requiere 6 meses de experiencia como docente/instructor en habilidades digitales.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={perfilCardStyle}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              EJECUTOR INGLÉS
            </Typography>
            <Typography variant="body2">
              Profesional en NBC (Educación, lenguas modernas, literatura o lingüística). 
              Requiere 3 años de experiencia en formación del idioma inglés.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={perfilCardStyle}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              EJECUTOR HABILIDADES DE PODER
            </Typography>
            <Typography variant="body2">
              Profesional en NBC (Psicología, sociología o trabajo social). 
              Requiere 2 años de experiencia profesional relacionada.
            </Typography>
          </CardContent>
        </Card>

        <Divider sx={{ my: isMobile ? 2 : 3 }} />

        <Typography variant={isMobile ? 'h6' : 'h5'} sx={sectionTitleStyle}>
          Información Importante
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="El sistema validará que cumplas con la experiencia mínima requerida para el perfil seleccionado." />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Si tienes experiencia adicional en docencia, esta puede complementar requisitos de experiencia no docente." />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Todos los documentos deben estar vigentes (no mayores a 30 días para certificados)." />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Asegúrate de tener a mano todos los documentos necesarios antes de comenzar." />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Tu información será valorada por el equipo técnico para determinar tu idoneidad para el cargo." />
          </ListItem>
        </List>

        <Box sx={{ 
          mt: isMobile ? 3 : 4, 
          p: 2, 
          backgroundColor: '#f9f9f9', 
          borderRadius: 2,
          textAlign: 'center' 
        }}>
          <Typography variant="h6" fontWeight="medium" sx={{ mb: 1, color: PRIMARIO }}>
            ¿Listo para comenzar?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <AccessTimeIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="body2">
              El proceso completo toma aproximadamente 20-30 minutos si tienes todos los documentos preparados.
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Tómate el tiempo necesario para completar cada sección con información precisa.
          </Typography>
          <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          type="submit"
          onClick={handleNext}
          endIcon={<Icon>chevron_right</Icon>}
          sx={{
            py: 1,
            px: 2,
          }}
        >
          Diligenciar formulario
        </Button>
      </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Recomendaciones;